import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AnalyticsKPITileProps {
  variant: 'hero' | 'secondary';
  label: string;
  value: number | string;
  deltaPct: number | null;
  format?: 'integer' | 'percent' | 'currency';
  icon?: LucideIcon;
  onClick?: () => void;
  isLoading: boolean;
  error: Error | null;
}

function formatValue(v: number | string, format?: 'integer' | 'percent' | 'currency'): string {
  if (typeof v === 'string') return v;
  if (format === 'percent') return `${v.toFixed(1)}%`;
  if (format === 'currency') return `$${v.toLocaleString()}`;
  return v.toLocaleString();
}

export function AnalyticsKPITile(props: AnalyticsKPITileProps) {
  const numberClass =
    props.variant === 'hero'
      ? 'text-3xl font-bold tabular-nums'
      : 'text-2xl font-bold tabular-nums';
  const colSpan = 'col-span-1';

  if (props.error) {
    return (
      <Card className={cn('p-4', colSpan)}>
        <div className="text-sm text-muted-foreground">{props.label}</div>
        <div className="flex items-center gap-2 mt-2 text-destructive">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm">Couldn't load</span>
        </div>
      </Card>
    );
  }

  const isClickable = !!props.onClick;
  const Icon = props.icon;

  return (
    <Card
      className={cn(
        'p-4 transition-colors',
        colSpan,
        isClickable && 'cursor-pointer hover:bg-muted/50',
      )}
      onClick={props.onClick}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{props.label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      {props.isLoading ? (
        <Skeleton className={cn('mt-2', props.variant === 'hero' ? 'h-9 w-32' : 'h-7 w-20')} />
      ) : (
        <div className={cn('mt-2', numberClass)}>{formatValue(props.value, props.format)}</div>
      )}
      {props.deltaPct !== null && !props.isLoading && (
        <div
          className={cn(
            'flex items-center gap-1 mt-1 text-sm',
            props.deltaPct >= 0 ? 'text-green-600' : 'text-destructive',
          )}
        >
          {props.deltaPct >= 0 ? (
            <TrendingUp className="h-3 w-3" />
          ) : (
            <TrendingDown className="h-3 w-3" />
          )}
          {Math.abs(props.deltaPct).toFixed(1)}% vs prev period
        </div>
      )}
      {props.deltaPct === null &&
        !props.isLoading &&
        typeof props.value === 'number' &&
        props.value > 0 && (
          <div className="mt-1 text-sm text-muted-foreground">New</div>
        )}
    </Card>
  );
}
